/**
 * Retold Application (client chassis)
 *
 * The reusable browser-side base class for a Retold app. It extends
 * pict-section-recordset's application, so all entity CRUD comes from that
 * module rather than being reimplemented here:
 *
 *   pict-section-recordset  ->  list / read / create / edit / dashboard,
 *                               filters, column chooser, soft-delete handling,
 *                               and join-table association management. Its
 *                               MeadowEndpoint record provider derives columns
 *                               from the /1.0/<Entity>/Schema endpoint that the
 *                               foundation's server chassis already mounts.
 *
 *   this chassis            ->  the app frame around it: the modal shell
 *                               (top bar / sidebar / workspace), theming,
 *                               navigation, inline help, and SHARED AUTH
 *                               (session client + sign-in view + boot flow).
 *
 * That split is the whole point: the recordset module's own example app
 * hand-rolls a Layout, Navigation and Login view, which is exactly the
 * duplication an app should never have to write again.
 *
 * An app supplies its data: { Entities (or RecordSets), SiteMap, Brand }.
 *
 * Boot handshake (unchanged):
 *   Pict.safeLoadPictApplication(window.MyApp, 1);
 */

const libRecordSet = require('pict-section-recordset');

const libPictRouter = require('pict-router');
const libPictSectionModal = require('pict-section-modal');
const libPictSectionNavigation = require('pict-section-navigation');
const libPictSectionTheme = require('pict-section-theme');
const libInlineDocumentation = require('pict-section-inlinedocumentation');
const libPictSectionPicker = require('pict-section-picker');

const libShellView = require('./views/PictView-Retold-Shell.js');
const libLoginView = require('./views/PictView-Retold-Login.js');
const libSetupView = require('./views/PictView-Retold-Setup.js');
const libProfileView = require('./views/PictView-Retold-Profile.js');
const libNotificationsView = require('./views/PictView-Retold-Notifications.js');
const libTwoFactorView = require('./views/PictView-Retold-TwoFactor.js');
const libProviderAuthClient = require('./providers/Retold-Provider-AuthClient.js');

class RetoldApplication extends libRecordSet.PictRecordSetApplication
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		// This chassis renders its own shell (and the sign-in view) in
		// onAfterInitializeAsync, so pict's main-viewport autorender is off; leaving
		// it on makes boot fail trying to render a Default-View that does not exist.
		this.options.AutoRenderMainViewportViewAfterInitialize = false;

		// 1. The record-set configuration has to be in fable settings BEFORE
		//    onInitialize(), which is when the recordset metacontroller reads it.
		this._applyRecordSetSettings();

		// 2. The router is registered here, ahead of recordset initialization, so the
		//    recordset router reuses it instead of creating its own. Route resolution
		//    is deferred (SkipRouteResolveOnAdd) until the shell has rendered.
		if (!this.pict.providers[this.routerHash()])
		{
			this.pict.addProvider(this.routerHash(), this.routerOptions(), libPictRouter);
		}

		// 3. The modal section supplies the shell layout engine.
		this.pict.addView('Pict-Section-Modal', libPictSectionModal.default_configuration, libPictSectionModal);
		this._registerDefaultShell();

		// 4. Shared auth: the session client + the sign-in view (opt-in via Auth).
		this._registerAuth();

		// 4b. The shared self-service profile view. Reached at /Profile when the app links
		//     to it and the server has mounted the profile routes.
		this._registerProfile();

		// 4c. The shared comms self-service views: notification preferences and two-step
		//     verification enrollment. Reached at /Notifications and /Security when the app
		//     links to them and the server has the comms subsystem on. Each shows a plain
		//     "not available" message when its server route is off.
		this._registerNotifications();
		this._registerTwoFactor();

		// 5. The app's own providers and views.
		this.registerProviders();
		this.registerViews();

		// 5b. The searchable entity picker the record-set association editor uses. It is a
		//     soft dependency of pict-section-recordset (the host registers it), so the
		//     chassis wires it here, letting an app's association tabs add links out of the box.
		if (!this.pict.providers['Pict-Section-Picker'])
		{
			this.pict.addProvider('Pict-Section-Picker', libPictSectionPicker.default_configuration, libPictSectionPicker);
		}

		// 6. Navigation, inline documentation, theme.
		this.pict.addProvider('Pict-Navigation', { NavigationGraph: this.navigationGraph() }, libPictSectionNavigation);
		if (this.pict.providers['Pict-Navigation'])
		{
			this.pict.providers['Pict-Navigation'].onNavigate = (pNode) => this.onNavigate(pNode);
		}
		this.pict.addProvider('Pict-InlineDocumentation', libInlineDocumentation.default_configuration, libInlineDocumentation);
		this._registerTheme();
	}

	// --------------------------------------------------------------------------
	// Overridable hooks. Defaults read the app's default_configuration.
	// --------------------------------------------------------------------------

	// Accepts either a map of descriptors or a plain list of entity names (what the
	// server's AppManifest reports), normalizing to a keyed map.
	entities()
	{
		let tmpEntities = this.options.Entities || {};
		if (!Array.isArray(tmpEntities)) { return tmpEntities; }
		let tmpNormalized = {};
		tmpEntities.forEach((pEntity) =>
		{
			if (typeof pEntity === 'string') { tmpNormalized[pEntity] = { Key: pEntity }; }
			else if (pEntity && pEntity.Key) { tmpNormalized[pEntity.Key] = pEntity; }
		});
		return tmpNormalized;
	}

	// The per-entity schemas the server bundled into the manifest (keyed by entity name),
	// so a record set need not fetch /1.0/<Entity>/Schema on boot. Empty when the manifest
	// carried none (an older server), in which case record sets fetch as before.
	entitySchemas()      { return this.options.EntitySchemas || {}; }
	brand()              { return this.options.Brand || null; }
	navigationGraph()    { return this.options.SiteMap || this.options.NavigationGraph || []; }
	themeCatalog()       { return this.options.ThemeCatalog || []; }
	themeConfiguration() { return this.options.ThemeConfiguration || {}; }
	apiPrefix()          { return this.options.APIPrefix || '/1.0/'; }
	routerHash()         { return this.options.RouterHash || 'PictRouter'; }
	routerOptions()      { return Object.assign({ SkipRouteResolveOnAdd: true }, this.options.RouterOptions || {}); }
	shellViewHash()      { return this.options.ShellViewHash || 'Retold-Application-Shell'; }
	loginViewHash()      { return this.options.LoginViewHash || 'Retold-Login'; }
	// The first-run setup view (appliance install). Point at your own to fully replace it.
	setupViewHash()      { return this.options.SetupViewHash || 'Retold-Setup'; }
	// The server reports (manifest SetupRequired) that this is a fresh appliance with no account yet;
	// boot then shows the first-run setup view instead of sign-in.
	setupRequired()      { return !!this.options.SetupRequired; }
	authEnabled()        { return !!this.options.Auth; }
	// The server reports (via the manifest) whether the comms two-step login is on; when it
	// is, the auth client and the sign-in view use the password-then-code /Login flow.
	twoStepLogin()       { return !!this.options.AuthTwoStepLogin; }

	registerProviders() { }
	registerViews() { }

	// App-specific routes. The recordset module registers its own /PSRS/... routes.
	setupRoutes(pRouter) { }

	onNavigate(pNode) { if (pNode && pNode.Route) { this._navigate(String(pNode.Route).replace(/^#/, '')); } }
	onApplicationReadyAsync(fCallback) { return fCallback(); }

	// --------------------------------------------------------------------------
	// Record-set configuration
	// --------------------------------------------------------------------------

	/**
	 * The record-set configurations pict-section-recordset drives everything from.
	 * An app may supply them verbatim (full recordset power: filters, dashboards,
	 * association tabs), or lean on the concise Entities descriptors, which are
	 * translated into MeadowEndpoint record sets pointed at this app's API.
	 */
	recordSetConfigurations()
	{
		if (Array.isArray(this.options.RecordSets)) { return this.options.RecordSets; }
		if (Array.isArray(this.fable.settings.DefaultRecordSetConfigurations)) { return this.fable.settings.DefaultRecordSetConfigurations; }

		let tmpEntities = this.entities();
		let tmpSchemas = this.entitySchemas();
		return Object.keys(tmpEntities).map((pEntityKey) =>
		{
			let tmpDescriptor = tmpEntities[pEntityKey];
			let tmpConfiguration =
			{
				RecordSet: tmpDescriptor.Key,
				RecordSetType: 'MeadowEndpoint',
				RecordSetMeadowEntity: tmpDescriptor.MeadowEntity || tmpDescriptor.Key,
				RecordSetURLPrefix: this.apiPrefix()
			};
			// Hand the record set its schema when the manifest bundled one (an explicit
			// per-descriptor Schema wins). With none, the record set fetches /1.0/<Entity>/Schema
			// on boot exactly as before -- the fallback that keeps an older server working.
			let tmpProvidedSchema = tmpDescriptor.Schema || tmpSchemas[pEntityKey] || tmpSchemas[tmpDescriptor.MeadowEntity || tmpDescriptor.Key];
			if (tmpProvidedSchema) { tmpConfiguration.RecordSetSchema = tmpProvidedSchema; }
			if (tmpDescriptor.Title) { tmpConfiguration.Title = tmpDescriptor.Title; }
			if (tmpDescriptor.Columns && tmpDescriptor.Columns.length)
			{
				tmpConfiguration.RecordSetListColumns = tmpDescriptor.Columns.map((pColumn) =>
					({ Key: pColumn.F, DisplayName: pColumn.L || pColumn.F }));
			}
			if (tmpDescriptor.SearchFields) { tmpConfiguration.SearchFields = tmpDescriptor.SearchFields; }
			tmpConfiguration.RecordSetIgnoreFilterFields = this.ignoreFilterFields(tmpDescriptor);
			return tmpConfiguration;
		});
	}

	/**
	 * The columns a record set should not offer as filters. This is ordinary app-author
	 * configuration -- the same `RecordSetIgnoreFilterFields` a hand-written pict app
	 * (e.g. headlight-platform-configuration) lists on every record set. It is not
	 * tenancy logic and it is not the recordset module's concern: the module just honors
	 * whatever list the app hands it.
	 *
	 * The list is the columns that are structural rather than user-facing: the row's own
	 * identity (`ID<Entity>` / `GUID<Entity>`), the soft-delete plumbing, the external-sync
	 * stamps, and `IDCustomer`. `IDCustomer` is a normal scoped column the server manages
	 * from the session, so a user filtering on it is meaningless -- every app on
	 * meadow-endpoints simply leaves it out of its filters, exactly here. (The audit
	 * author stamps CreatingIDUser / UpdatingIDUser are deliberately NOT excluded -- they
	 * make good "Created by" / "Updated by" filters.)
	 *
	 * @param {object} pDescriptor - The entity descriptor (carries the entity Key, and may
	 *        carry its own IgnoreFilterFields override).
	 * @return {Array<string>} The field names to skip.
	 */
	ignoreFilterFields(pDescriptor)
	{
		let tmpDescriptor = pDescriptor || {};
		if (Array.isArray(tmpDescriptor.IgnoreFilterFields)) { return tmpDescriptor.IgnoreFilterFields; }
		let tmpEntity = tmpDescriptor.Key || tmpDescriptor.MeadowEntity || '';
		return [
			'ID' + tmpEntity,
			'GUID' + tmpEntity,
			'IDCustomer',
			'Deleted',
			'DeleteDate',
			'DeletingIDUser',
			'ExternalSyncDate',
			'ExternalSyncGUID'
		];
	}

	_applyRecordSetSettings()
	{
		let tmpConfigurations = this.recordSetConfigurations();
		if (tmpConfigurations && tmpConfigurations.length)
		{
			this.fable.settings.DefaultRecordSetConfigurations = tmpConfigurations;
		}
		// Manifests / Filters / Associations pass straight through to the recordset
		// module, which reads them off fable settings.
		['Manifests', 'Filters', 'FilterCriteria', 'Associations', 'RecordCards'].forEach((pKey) =>
		{
			if (this.options[pKey])
			{
				this.fable.settings[pKey] = Object.assign({}, this.fable.settings[pKey], this.options[pKey]);
			}
		});
	}

	// --------------------------------------------------------------------------
	// Registration helpers
	// --------------------------------------------------------------------------

	_registerDefaultShell()
	{
		if (this.pict.views[this.shellViewHash()]) { return; }
		this.pict.addView(this.shellViewHash(),
			Object.assign({}, libShellView.default_configuration,
				{ Brand: this.brand(), NavigationGraph: this.navigationGraph() }),
			libShellView);
	}

	_registerAuth()
	{
		if (!this.authEnabled()) { return; }
		if (!this.pict.providers['Retold-AuthClient'])
		{
			this.pict.addProvider('Retold-AuthClient',
				Object.assign({}, libProviderAuthClient.default_configuration, { TwoStepLogin: this.twoStepLogin() }),
				libProviderAuthClient);
		}
		if (!this.pict.views[this.loginViewHash()])
		{
			this.pict.addView(this.loginViewHash(),
				Object.assign({}, libLoginView.default_configuration,
					{ Brand: this.brand(), TwoStepLogin: this.twoStepLogin(), OnAuthenticated: (pSession) => this.onAuthenticated(pSession) }),
				libLoginView);
		}
		// The shared first-run setup view (appliance install). Registered for every auth app; it is only
		// rendered when the manifest reports SetupRequired. Its copy/fields come from options.Setup (app or
		// manifest), layered over the view's neutral defaults. On success it hands off exactly like sign-in.
		if (!this.pict.views[this.setupViewHash()])
		{
			this.pict.addView(this.setupViewHash(),
				Object.assign({}, libSetupView.default_configuration,
					{
						Brand: this.brand(),
						Setup: this.options.Setup,
						OnAuthenticated: (pSession) => this.onAuthenticated(pSession),
						OnSetupCreated: () => this.renderLogin()
					}),
				libSetupView);
		}
	}

	// The shared self-service profile view. Registered for every app; it is only reached
	// when the app links to the /Profile route, and it shows a plain "not available" message
	// when the server has not mounted the profile routes. See PictView-Retold-Profile.
	_registerProfile()
	{
		if (this.pict.views['Retold-Profile']) { return; }
		this.pict.addView('Retold-Profile',
			Object.assign({}, libProfileView.default_configuration, { APIPrefix: this.apiPrefix() }),
			libProfileView);
	}

	// The shared notification-preferences view. Reached at /Notifications; shows a plain
	// message when the comms preference route is off. See PictView-Retold-Notifications.
	_registerNotifications()
	{
		if (this.pict.views['Retold-Notifications']) { return; }
		this.pict.addView('Retold-Notifications',
			Object.assign({}, libNotificationsView.default_configuration, { APIPrefix: this.apiPrefix() }),
			libNotificationsView);
	}

	// The shared two-step verification (2FA) enrollment view. Reached at /Security; shows a
	// plain message when the comms two-step routes are off. See PictView-Retold-TwoFactor.
	_registerTwoFactor()
	{
		if (this.pict.views['Retold-TwoFactor']) { return; }
		this.pict.addView('Retold-TwoFactor',
			Object.assign({}, libTwoFactorView.default_configuration, { APIPrefix: this.apiPrefix() }),
			libTwoFactorView);
	}

	_registerTheme()
	{
		let tmpConfiguration = Object.assign(
			{ DefaultMode: 'system', DefaultScale: 1.0, Views: ['Picker', 'ModeToggle', 'ScaleSelect', 'BrandMark'] },
			this.themeConfiguration());
		if (this.brand()) { tmpConfiguration.Brand = this.brand(); }

		let tmpCatalog = this.themeCatalog();
		if (Array.isArray(tmpCatalog) && tmpCatalog.length)
		{
			if (libPictSectionTheme.Catalog && typeof libPictSectionTheme.Catalog.clear === 'function')
			{
				libPictSectionTheme.Catalog.clear();
				tmpCatalog.forEach((pEntry) => libPictSectionTheme.Catalog.register(pEntry));
			}
			if (!tmpConfiguration.ApplyDefault) { tmpConfiguration.ApplyDefault = tmpCatalog[0].Hash; }
		}
		this.pict.addProvider('Theme-Section', tmpConfiguration, libPictSectionTheme);
	}

	// --------------------------------------------------------------------------
	// Rendering + boot
	// --------------------------------------------------------------------------

	renderShell()
	{
		try
		{
			let tmpShell = this.pict.views[this.shellViewHash()];
			if (tmpShell && typeof tmpShell.render === 'function') { tmpShell.render(); }
		}
		catch (pError)
		{
			this.log.error('RetoldApplication: shell render failed: ' + ((pError && pError.message) || pError));
		}
	}

	renderLogin()
	{
		try
		{
			let tmpLogin = this.pict.views[this.loginViewHash()];
			if (tmpLogin && typeof tmpLogin.render === 'function') { tmpLogin.render(); }
		}
		catch (pError)
		{
			this.log.error('RetoldApplication: login render failed: ' + ((pError && pError.message) || pError));
		}
	}

	renderSetup()
	{
		try
		{
			let tmpSetup = this.pict.views[this.setupViewHash()];
			if (tmpSetup && typeof tmpSetup.render === 'function') { tmpSetup.render(); }
		}
		catch (pError)
		{
			this.log.error('RetoldApplication: setup render failed: ' + ((pError && pError.message) || pError));
		}
	}

	onAuthenticated(pSession)
	{
		this.renderShell();
		this._navigateCurrent();
		return pSession;
	}

	logout()
	{
		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		if (!tmpAuthClient) { return Promise.resolve(null); }
		return tmpAuthClient.logout().then((pResult) =>
		{
			this.renderLogin();
			// Drop the deep link from the URL on sign-out. The SPA router will not re-fire a
			// route for a URL it has already resolved, so signing back in on the same URL
			// (e.g. #/Overview) rebuilds the shell but leaves the workspace blank. Clearing the
			// hash makes the next sign-in a genuine navigation from a bare URL -- the same path
			// a fresh page load takes -- which dispatches reliably. Assigning
			// window.location.hash (rather than history.replaceState) is deliberate: only the
			// former fires the hashchange the router listens for, so its own location cache
			// updates with it.
			if (typeof window !== 'undefined' && window.location && window.location.hash)
			{
				window.location.hash = '';
			}
			return pResult;
		});
	}

	/**
	 * Land on a real screen: honor a deep link when there is one, otherwise fall back
	 * to the first navigation entry.
	 *
	 * The router's own navigateCurrent() deliberately handles only a real deep link --
	 * it returns false for an empty hash or a bare "#/", which is the ordinary case on
	 * a fresh sign-in. Its return value has to be checked, or the fallback below never
	 * runs and the workspace sits empty until the user clicks something.
	 *
	 * @return {boolean} true when a route was navigated to.
	 */
	_navigateCurrent()
	{
		let tmpRouter = this._router();
		if (!tmpRouter) { return false; }
		if ((typeof tmpRouter.navigateCurrent === 'function') && tmpRouter.navigateCurrent()) { return true; }
		let tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash)
			? String(window.location.hash).replace(/^#/, '') : '';
		if (tmpHash && (tmpHash !== '/')) { this._navigate(tmpHash); return true; }
		return this._navigateDefault();
	}

	/**
	 * Whether the app currently has a session to render data with. Always true when
	 * auth is off, since there is nothing to gate on.
	 *
	 * @return {boolean}
	 */
	authenticated()
	{
		if (!this.authEnabled()) { return true; }
		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		if (!tmpAuthClient || (typeof tmpAuthClient.session !== 'function')) { return false; }
		return !!tmpAuthClient.session().LoggedIn;
	}

	/**
	 * The signed-in account's system role, read from the session (e.g. 'root' for the first-run
	 * administrator), or '' when there is no session. The server already exposes SystemRole on the
	 * session UserRecord; this is the client-side reader so views and route guards can gate on it.
	 */
	systemRole()
	{
		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		let tmpSession = (tmpAuthClient && typeof tmpAuthClient.session === 'function') ? tmpAuthClient.session() : null;
		return (tmpSession && tmpSession.UserRecord && tmpSession.UserRecord.SystemRole) || '';
	}

	// True when the signed-in account is a system administrator (SystemRole matches the configured
	// value, 'root' by default). Use it to show admin chrome and gate admin-only routes and nav.
	isSystemAdmin() { return this.systemRole() === (this.options.SystemAdminValue || 'root'); }

	/**
	 * Navigate to the app's default screen -- the first navigation entry.
	 *
	 * Refuses to move while signed out. A record-set route fetches immediately, so
	 * landing there without a session only buys a 401 and an empty screen -- and,
	 * worse, it leaves that URL as the browser's current route. Navigo will not re-run
	 * a route it has already resolved, so the navigation issued moments later on a
	 * successful sign-in would be silently dropped and the workspace would stay blank.
	 *
	 * @return {boolean} true when a route was navigated to.
	 */
	_navigateDefault()
	{
		if (!this.authenticated()) { return false; }
		let tmpGraph = this.navigationGraph();
		if (tmpGraph.length && tmpGraph[0] && tmpGraph[0].Route)
		{
			this._navigate(String(tmpGraph[0].Route).replace(/^#/, ''));
			return true;
		}
		return false;
	}

	/**
	 * Give "/" a home. pict-section-recordset registers /PSRS/... routes and nothing
	 * claims the root, so a boot at "/" makes navigo warn that the route did not match
	 * and leaves the workspace blank. Sending it to the default screen is what the user
	 * means by opening the app with no deep link.
	 */
	_registerDefaultRoute()
	{
		let tmpRouter = this._router();
		if (!tmpRouter || (typeof tmpRouter.addRoute !== 'function')) { return; }
		tmpRouter.addRoute('/', () => { this._navigateDefault(); });
	}

	// Give /Profile a home: render the shared self-service profile view into the workspace.
	_registerProfileRoute()
	{
		let tmpRouter = this._router();
		if (!tmpRouter || (typeof tmpRouter.addRoute !== 'function')) { return; }
		tmpRouter.addRoute('/Profile', () => { this._renderProfile(); });
	}

	_renderProfile()
	{
		let tmpView = this.pict.views['Retold-Profile'];
		if (tmpView && typeof tmpView.render === 'function') { tmpView.render(); }
	}

	// Give /Notifications and /Security homes: render the shared comms self-service views.
	_registerCommsRoutes()
	{
		let tmpRouter = this._router();
		if (!tmpRouter || (typeof tmpRouter.addRoute !== 'function')) { return; }
		tmpRouter.addRoute('/Notifications', () => { this._renderView('Retold-Notifications'); });
		tmpRouter.addRoute('/Security', () => { this._renderView('Retold-TwoFactor'); });
	}

	_renderView(pViewHash)
	{
		let tmpView = this.pict.views[pViewHash];
		if (tmpView && typeof tmpView.render === 'function') { tmpView.render(); }
	}

	// Boot: with auth off, render the shell. With auth on, ask the server whether
	// there is a session and render the shell or the sign-in view accordingly.
	//
	// Whenever the shell goes up, the app then navigates -- a boot with a live session
	// has to land somewhere by itself, since onAuthenticated() (the other caller) only
	// runs on an interactive sign-in.
	onAfterInitializeAsync(fCallback)
	{
		let fFinish = (pShellRendered) =>
		{
			this.setupRoutes(this._router());
			this._registerDefaultRoute();
			this._registerProfileRoute();
			this._registerCommsRoutes();
			return this.onApplicationReadyAsync(() => super.onAfterInitializeAsync(() =>
			{
				if (pShellRendered) { this._navigateCurrent(); }
				return fCallback();
			}));
		};

		if (!this.authEnabled()) { this.renderShell(); return fFinish(true); }

		// Fresh appliance: the server reports no account exists yet, so show first-run setup rather than
		// sign-in. SetupRequired comes from the manifest, which a fresh boot fetched moments ago.
		if (this.setupRequired()) { this.renderSetup(); return fFinish(false); }

		let tmpAuthClient = this.pict.providers['Retold-AuthClient'];
		if (!tmpAuthClient) { this.renderShell(); return fFinish(true); }

		return tmpAuthClient.checkSession()
			.then((pSession) =>
			{
				let tmpLoggedIn = !!(pSession && pSession.LoggedIn);
				if (tmpLoggedIn) { this.renderShell(); }
				else { this.renderLogin(); }
				return fFinish(tmpLoggedIn);
			})
			.catch(() => { this.renderLogin(); return fFinish(false); });
	}

	// --------------------------------------------------------------------------
	// Helpers
	// --------------------------------------------------------------------------

	_router() { return this.pict.providers[this.routerHash()]; }

	_navigate(pRoute)
	{
		let tmpRouter = this._router();
		if (tmpRouter && typeof tmpRouter.navigate === 'function') { tmpRouter.navigate(pRoute); }
	}
}

module.exports = RetoldApplication;
// Re-exported so an app can reach the record-set building blocks (custom record
// providers, association manager) without depending on the module directly.
module.exports.RecordSet = libRecordSet;

// Manifest boot: builds and starts an app from the server's /1.0/AppManifest, so
// an app needs no client JavaScript. Required lazily to avoid a require cycle.
module.exports.boot = function (pOptions) { return require('./Retold-Application-Boot.js').boot(pOptions); };
module.exports.bootWithManifest = function (pManifest, pOptions) { return require('./Retold-Application-Boot.js').bootWithManifest(pManifest, pOptions); };
module.exports.applicationClassFromManifest = function (pManifest, pOptions) { return require('./Retold-Application-Boot.js').applicationClassFromManifest(pManifest, pOptions); };
